import { useLayoutEffect, useState, type RefObject } from "react";

export function selectViewportMenu(top: number, bottom: number, viewportTop: number, viewportHeight: number) {
    const above = Math.max(0, top - viewportTop - 8);
    const below = Math.max(0, viewportTop + viewportHeight - bottom - 8);
    const placement = below >= Math.min(240, above) ? "bottom" : "top";
    return { placement, maxHeight: Math.max(1, Math.min(240, placement === "bottom" ? below : above)) } as const;
}

// VisualViewport shrinks when a mobile keyboard opens; innerHeight may not.
export default function useSelectViewport(ref: RefObject<{controlRef: HTMLDivElement | null} | null>, open: boolean, enabled: boolean) {
    const [menu, setMenu] = useState<{placement: "top" | "bottom" | "auto"; maxHeight: number; mobile: boolean}>({placement: "auto", maxHeight: 300, mobile: false});
    useLayoutEffect(() => {
        if (!enabled || !open) return;
        const viewport = window.visualViewport;
        const update = () => {
            const control = ref.current?.controlRef;
            if (!control) return;
            const rect = control.getBoundingClientRect();
            const mobile = window.matchMedia("(max-width: 67rem)").matches;
            const next = {mobile, ...(mobile
                ? selectViewportMenu(rect.top, rect.bottom, viewport?.offsetTop ?? 0, viewport?.height ?? window.innerHeight)
                : {placement: "bottom" as const, maxHeight: 300})};
            setMenu(previous => previous.placement === next.placement && previous.maxHeight === next.maxHeight && previous.mobile === next.mobile ? previous : next);
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        viewport?.addEventListener("resize", update);
        viewport?.addEventListener("scroll", update);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
            viewport?.removeEventListener("resize", update);
            viewport?.removeEventListener("scroll", update);
        };
    }, [enabled, open, ref]);
    return menu;
}
